import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Budgets() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "", amount: "" });

  const load = async () => {
    try {
      const [b, c] = await Promise.all([
        api.get("/insights/budget-progress"),
        api.get("/categories"),
      ]);
      setItems(b.data);
      setCats(c.data.filter((x) => x.type === "expense"));
    } catch (e) {
      console.warn("Budgets load failed", e?.response?.status);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.amount || Number(form.amount) <= 0) {
      toast.error("Pick a category and amount");
      return;
    }
    try {
      await api.post("/budgets", {
        category: form.category,
        amount: Number(form.amount),
      });
      toast.success("Budget saved");
      setForm({ category: "", amount: "" });
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save budget");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this budget?")) return;
    await api.delete(`/budgets/${id}`);
    toast.success("Budget removed");
    load();
  };

  return (
    <div data-testid="budgets-page">
      <SectionHeader
        overline="Discipline"
        title="Monthly Budgets"
        description="Set a calm cap per category. We'll track this month's spend."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-add-budget"
                className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add budget
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight font-medium">
                  Set a budget
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="budget-category">
                      <SelectValue placeholder="Choose category" />
                    </SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Monthly limit (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="mt-1.5 font-mono-num"
                    data-testid="budget-amount"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-budget"
                    className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <div className="bg-white border border-[#EAE8E3] rounded-lg p-12 text-center text-sm text-[#696E69]">
          No budgets yet. Add one to keep an eye on your spend.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((b) => {
            const pct = Math.min(b.percentage, 999);
            const over = b.spent > b.amount;
            return (
              <div
                key={b.id}
                className="bg-white border border-[#EAE8E3] rounded-lg p-6"
                data-testid={`budget-card-${b.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="label-overline mb-1">{b.category}</div>
                    <div className="font-mono-num text-2xl tracking-tight">
                      ₹{formatINR(b.spent)}
                      <span className="text-[#696E69] text-sm ml-1">
                        / ₹{formatINR(b.amount)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(b.id)}
                    data-testid={`delete-budget-${b.id}`}
                    className="p-2 rounded-md hover:bg-[#D26A59]/10 text-[#D26A59]"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="h-2 w-full bg-[#F3F1ED] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      over ? "bg-[#D26A59]" : pct > 75 ? "bg-[#E8B253]" : "bg-[#5C8065]"
                    }`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-[#696E69] font-mono-num">
                  <span>{pct.toFixed(0)}% used</span>
                  <span>
                    {over ? "Over by " : "Left "} ₹{formatINR(Math.abs(b.remaining))}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
